import { useTranslation } from '../../core/hooks/use-translation';
import { company } from '../../site/company';
import { PhoneIcon } from './policy-icons';
import type { PolicySection } from './policy-sections';
import type { PolicyTab } from './policy-tab';

interface PolicySidebarProps {
    sections: readonly PolicySection[];
    tab: PolicyTab;
    /** The anchor id the reader is currently inside, from `useActiveSection`. */
    activeId: string | null;
}

/**
 * The contents rail beside the document, and the WhatsApp card under it.
 *
 * **The entries are the sections' own labels, numbered by position.** The live page keeps a second
 * hand-numbered list in its script, so the rail and the badge on the section it points at disagree
 * in four places out of sixteen and the numbering has to be renumbered by hand whenever a section
 * moves.
 *
 * Below 900px the stylesheet turns the whole rail into a row of pills and hides the card — it is
 * the widescreen page's first column, and on a telephone the document is the page.
 */
export function PolicySidebar({ sections, tab, activeId }: PolicySidebarProps) {
    const { t } = useTranslation();

    return (
        <aside className="sidebar">
            <p className="sidebar-title">{t('policy.side.contents')}</p>
            <nav className="sidebar-nav" aria-label={t('policy.side.contents')}>
                {sections.map((section, index) => {
                    const anchor = `${tab}-${section.id}`;

                    return (
                        <a
                            key={section.id}
                            href={`#${anchor}`}
                            className={anchor === activeId ? 'sidebar-link active' : 'sidebar-link'}
                            aria-current={anchor === activeId ? 'true' : undefined}
                        >
                            {index + 1}. {t(section.label)}
                        </a>
                    );
                })}
            </nav>

            <div className="sidebar-divider" />

            <div className="sidebar-cta">
                <p className="sidebar-cta-title">{t('policy.side.cta_title')}</p>
                <p className="sidebar-cta-sub">{t('policy.side.cta_sub')}</p>
                <a href={company.whatsApp} target="_blank" rel="noreferrer noopener">
                    <PhoneIcon />
                    <span>{t('policy.side.whatsapp')}</span>
                </a>
            </div>
        </aside>
    );
}
